import type { CastRefEventStore } from "applesauce-common/casts/cast";
import { useActiveAccount } from "applesauce-react/hooks";
import { mapEventsToStore } from "applesauce-core";
import { getSeenRelays, type Filter } from "applesauce-core/helpers";
import { onlyEvents } from "applesauce-relay";
import { combineLatest, EMPTY, timer } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { CIRun, isValidCIRun } from "@/casts/CIRun";
import { CIResult, isValidCIResult } from "@/casts/CIResult";
import { use$ } from "@/hooks/use$";
import type { CICoordinatorProfileState } from "@/hooks/useCICoordinatorProfile";
import { useEventStore } from "@/hooks/useEventStore";
import { useGraspServers } from "@/hooks/useGraspServers";
import {
  CI_RESULT_KIND,
  CI_RUN_KIND,
  groupCIWorkflowRuns,
  type CIWorkflowRun,
} from "@/lib/ci";
import { resilientSubscription } from "@/lib/resilientSubscription";
import { normalizeUrl } from "@/lib/url";
import { pool } from "@/services/nostr";
import { gitIndexRelays, lookupRelays } from "@/services/settings";

const RECENT_RUN_LIMIT = 8;
const EXPIRATION_RECHECK_MS = 30_000;

/** Recent workflow runs signed by this coordinator and observed on relevant Git relays. */
export function useCoordinatorRecentRuns(
  pubkey: string | undefined,
  state: CICoordinatorProfileState | undefined,
  serviceRelayUrl: string | undefined,
): CIWorkflowRun[] | undefined {
  const store = useEventStore();
  const castStore = store as unknown as CastRefEventStore;
  const account = useActiveAccount();
  const { servers } = useGraspServers(account?.pubkey);
  const indexes = use$(() => gitIndexRelays, []) ?? [];
  const lookups = use$(() => lookupRelays, []) ?? [];
  const relays = [
    ...new Set([
      ...(serviceRelayUrl ? [serviceRelayUrl] : []),
      ...(state?.outboxes ?? []),
      ...(state?.targetedRepositories ?? []).flatMap(
        (repository) => repository.relays,
      ),
      ...servers.map((server) => server.wsUrl),
      ...indexes,
      ...lookups,
    ]),
  ].slice(0, 16);
  const relayKey = [...relays].sort().join(",");

  use$(() => {
    if (!pubkey || relays.length === 0) return undefined;
    return resilientSubscription(
      pool,
      relays,
      [
        {
          kinds: [CI_RESULT_KIND, CI_RUN_KIND],
          authors: [pubkey],
          limit: 32,
        } as Filter,
      ],
      { paginate: false },
    ).pipe(
      onlyEvents(),
      mapEventsToStore(store),
      catchError(() => EMPTY),
    );
  }, [pubkey, relayKey, store]);

  return use$(() => {
    if (!pubkey || relays.length === 0) return undefined;
    const relaySet = new Set(relays.map(normalizeUrl));
    return combineLatest([
      store.timeline([
        {
          kinds: [CI_RESULT_KIND, CI_RUN_KIND],
          authors: [pubkey],
        } as Filter,
      ]),
      timer(0, EXPIRATION_RECHECK_MS),
    ]).pipe(
      map(([events]) => {
        const observed = events.filter((event) =>
          [...(getSeenRelays(event) ?? [])].some((relay) =>
            relaySet.has(normalizeUrl(relay)),
          ),
        );
        const runs = observed.flatMap((event) =>
          isValidCIRun(event) ? [new CIRun(event, castStore)] : [],
        );
        const results = observed.flatMap((event) =>
          isValidCIResult(event) ? [new CIResult(event, castStore)] : [],
        );
        return groupCIWorkflowRuns(runs, results, []).slice(
          0,
          RECENT_RUN_LIMIT,
        );
      }),
    );
  }, [pubkey, relayKey, store]);
}
